import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  FormField,
  FileUpload,
  Toggle,
  Alert,
  Skeleton,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { uploadProductImage } from './product-image-upload';
import { MAX_RAW_UPLOAD_BYTES } from '@/lib/upload-file';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import { rupeeInputToPaise } from '@/utils/price-input';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { formatPaise, marginPercent } from '@/utils/currency';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { StockAdjustModal } from './products/StockAdjustModal';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';

/**
 * Editing one variant of a parent product.
 *
 * Variants previously had no edit surface at all: opening one landed on the
 * normal product page, whose Variants tab only said "edit the parent". This
 * page carries just the fields that differ per variant — the parent's name,
 * category, tax and unit stay on the parent and are shown read-only for
 * context rather than duplicated as editable inputs.
 */

/** Stock is adjusted through stock movements, so its input is display-only. */
const noop = () => undefined;

/** "1.5 sq.mm / Red" — the variant's attribute values, in insertion order. */
function attributeLabel(attrs: Record<string, unknown> | undefined): string {
  if (!attrs) return '';
  return Object.values(attrs).map((v) => String(v)).join(' / ');
}

export function ProductVariantEditPage() {
  const { id, variantId } = useParams<{ id: string; variantId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  // Minor units per major unit for this tenant (INR 100, OMR/BHD/KWD 1000).
  const subunit = getSubunitMultiplier(currency);
  // "0.00" for INR, "0.000" for OMR — the price and cost input hint.
  const moneyPlaceholder = (0).toFixed(getCurrencyDecimals(currency));

  const { data: parent } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getById(id!),
    enabled: !!id,
  });

  const {
    data: variant,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['product', variantId],
    queryFn: () => productService.getById(variantId!),
    enabled: !!variantId,
  });

  const [barcode, setBarcode] = useState('');
  const [cost, setCost] = useState('');
  // The variant's own selling price (OD5): a parent price never moves it.
  const [priceInput, setPriceInput] = useState('');
  const [threshold, setThreshold] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [imageUploading, setImageUploading] = useState(false);
  const [showStockAdjust, setShowStockAdjust] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Saving no longer leaves the page, so a save refetches the variant underneath
  // an open form. Gate hydration on this the way ProductDetailPage does, or a
  // refetch landing mid-edit overwrites whatever is being typed.
  const [formDirty, setFormDirty] = useState(false);
  // Leaving with unsaved edits asks first (links, breadcrumb buttons, Cancel).
  const { confirmLeave, dialog: leaveDialog } = useUnsavedChangesGuard(formDirty);

  /** Wrap a field setter so touching it marks the form dirty. */
  function edited<T>(setter: (value: T) => void) {
    return (value: T) => {
      setFormDirty(true);
      setter(value);
    };
  }

  // Seed from the server whenever the form has no unsaved edits. Prices are
  // stored in minor units.
  useEffect(() => {
    if (!variant || formDirty) return;
    setBarcode(variant.barcode ?? '');
    setCost(variant.cost != null ? String(variant.cost / subunit) : '');
    setPriceInput(String((variant.price ?? 0) / subunit));
    setThreshold(
      variant.low_stock_threshold != null ? String(variant.low_stock_threshold) : '',
    );
    // `own_image_url` only — `image_url` may be the parent's picture inherited
    // as a fallback, and hydrating that would silently copy it onto the variant
    // on the next save, permanently breaking the inheritance.
    setImageUrl(variant.own_image_url ?? '');
    setIsActive(variant.is_active !== false);
  }, [variant, subunit, formDirty]);

  // This variant's rows at every location, for the Adjust stock location picker.
  const { data: stockRows } = useQuery({
    queryKey: ['product', variantId, 'stock'],
    queryFn: () => productService.getStock(variantId!),
    enabled: !!variantId && showStockAdjust,
  });

  // The typed selling price, for the profit line under the field.
  const effectivePricePaise = rupeeInputToPaise(priceInput, currency) ?? 0;
  const costPaise = rupeeInputToPaise(cost, currency);

  const save = useMutation({
    mutationFn: () =>
      // SKU is fixed after creation (not in the update payload).
      productService.update(variantId!, {
        is_active: isActive,
        // An emptied barcode is sent as '' and stored as NULL (CS2-002).
        barcode: barcode.trim(),
        cost: rupeeInputToPaise(cost, currency) ?? undefined,
        // The variant's own price; a blank field leaves it as it is.
        price: rupeeInputToPaise(priceInput, currency) ?? undefined,
        // A typed 0 is 0; an emptied field saves the default 10 (as on create).
        low_stock_threshold: threshold.trim() === '' ? 10 : Number(threshold),
        // A removed own picture is sent as '' and cleared. A variant that only
        // inherits the parent's picture sends nothing, so the fallback stays.
        image_url: imageUrl || (variant?.own_image_url ? '' : undefined),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', variantId] });
      queryClient.invalidateQueries({ queryKey: ['product', id, 'variants'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      // Clearing the flag deliberately re-opens hydration, so the refetched
      // canonical values (normalised numbers, the resolved image) flow back in.
      setFormDirty(false);
      toast({ title: 'Variant saved', variant: 'success' });
    },
    onError: (err: Error) => {
      // Surface the API's own message — duplicate SKU/barcode land here.
      toast({ title: err.message ?? 'Failed to save variant', variant: 'error' });
    },
  });

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (priceInput.trim() && (Number.isNaN(Number(priceInput)) || Number(priceInput) < 0)) {
      next.price = 'Enter a valid selling price';
    }
    if (cost.trim() && (Number.isNaN(Number(cost)) || Number(cost) < 0)) {
      next.cost = 'Enter a valid cost';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSave() {
    if (validate()) save.mutate();
  }

  /**
   * Save (when there is anything to save) and continue to the parent's variant
   * matrix. `addVariants=1` tells ProductDetailPage to open the generator once
   * the existing attributes have loaded, so the new combinations lock to them.
   */
  async function handleSaveAndAddAnother() {
    if (formDirty) {
      if (!validate()) return;
      try {
        await save.mutateAsync();
      } catch {
        return; // onError already surfaced the reason; stay put.
      }
    }
    navigate(`/products/${id}?tab=variants&addVariants=1`);
  }

  async function handleImage(files: File[]) {
    if (files.length === 0) {
      setFormDirty(true);
      setImageUrl('');
      return;
    }
    setImageUploading(true);
    try {
      setFormDirty(true);
      setImageUrl(await uploadProductImage(files[0]));
    } catch {
      toast({ title: 'Image upload failed', variant: 'error' });
    } finally {
      setImageUploading(false);
    }
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="space-y-4">
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="rect" height={320} />
        </div>
      </PageContainer>
    );
  }

  if (error || !variant) {
    return (
      <PageContainer>
        <Alert
          variant="danger"
          description="This variant could not be loaded."
          action={{ label: 'Back to product', onClick: () => navigate(`/products/${id}`) }}
        />
      </PageContainer>
    );
  }

  const combination = attributeLabel(variant.variant_attributes);

  return (
    <ErrorBoundary>
      <PageContainer>
        {/* Breadcrumb */}
        <nav className="mb-4 flex items-center gap-2 text-sm" aria-label="Breadcrumb">
          <button
            type="button"
            onClick={() => confirmLeave(() => navigate('/products'))}
            className="text-brand-600 hover:underline dark:text-brand-400"
          >
            Products
          </button>
          <span className="text-neutral-400">/</span>
          <button
            type="button"
            onClick={() => confirmLeave(() => navigate(`/products/${id}`))}
            className="text-brand-600 hover:underline dark:text-brand-400"
          >
            {parent?.name ?? 'Product'}
          </button>
          <span className="text-neutral-400">/</span>
          <span className="text-neutral-600 dark:text-neutral-300">{combination || 'Variant'}</span>
        </nav>

        {/* Parent context — read-only, not duplicated as editable fields */}
        <div className={cn(GLASS_FORM, 'mb-5 p-5')}>
          <p className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            Product
          </p>
          <h1 className="mt-1 text-xl font-semibold text-neutral-900 dark:text-white">
            {parent?.name ?? variant.name}
          </h1>
          {combination && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
              <span>Variant</span>
              {Object.entries(variant.variant_attributes ?? {}).map(([name, value]) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-3 py-1 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                >
                  <span className="opacity-70">{name}:</span> {String(value)}
                </span>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
            Name, category, unit and tax are set on the parent product and apply to every
            variant — renaming the product renames all of them.
          </p>
        </div>

        {/* Variant-specific fields */}
        <div className={cn(GLASS_FORM, 'space-y-5 p-5')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="SKU" htmlFor="v-sku" helperText="SKU is fixed after creation">
              <Input id="v-sku" value={variant.sku ?? ''} onChange={noop} disabled />
            </FormField>
            <FormField label="Barcode" htmlFor="v-barcode" error={errors.barcode}>
              <Input id="v-barcode" value={barcode} onChange={edited(setBarcode)} placeholder="Optional" />
            </FormField>
          </div>

          {/* POS comments item 3: a variant is priced on its own — selling price
              and cost, with the profit they give. The stored extra (price −
              product price) is kept in step behind the scenes. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label={`Selling Price (${symbol})`} htmlFor="v-price" error={errors.price}>
              <Input id="v-price" value={priceInput} onChange={edited(setPriceInput)} placeholder={moneyPlaceholder} />
            </FormField>
            <FormField label={`Cost Price (${symbol})`} htmlFor="v-cost" error={errors.cost}>
              <Input id="v-cost" value={cost} onChange={edited(setCost)} placeholder={moneyPlaceholder} />
            </FormField>
          </div>

          {/* Profit at the current price and cost — the same indicator the
              product create page shows, before tax. */}
          {effectivePricePaise > 0 && costPaise !== null && costPaise > 0 && (
            <div
              className={cn(
                'flex items-center gap-3 rounded-xl p-4',
                effectivePricePaise > costPaise
                  ? 'bg-emerald-50/80 dark:bg-emerald-900/20'
                  : 'bg-rose-50/80 dark:bg-rose-900/20',
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                  Profit: {formatPaise(effectivePricePaise - costPaise, currency)}
                </p>
                <p className="text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
                  {marginPercent(effectivePricePaise, costPaise)}% margin (before tax)
                </p>
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Stock"
              htmlFor="v-stock"
              helperText="Changed through stock movements, so every change is recorded."
            >
              <div className="flex gap-2">
                <Input id="v-stock" value={String(variant.stock_quantity ?? 0)} onChange={noop} disabled />
                <Button variant="secondary" onClick={() => setShowStockAdjust(true)}>
                  Adjust stock
                </Button>
              </div>
            </FormField>
            <FormField label="Minimum Stock" htmlFor="v-threshold">
              <Input
                id="v-threshold"
                value={threshold}
                onChange={edited((v: string) => setThreshold(v.replace(/\D/g, '')))}
                placeholder="10"
              />
            </FormField>
          </div>

          <div className="rounded-xl border border-neutral-200 bg-white/60 p-4 dark:border-neutral-700 dark:bg-neutral-900/40">

            <Toggle

              checked={isActive}

              onChange={edited(setIsActive)}

              label="Active"

              description={parent?.is_active === false
                ? 'The parent product is deactivated — activate it first to switch this variant on.'
                : 'Switch off to hide this variant from the POS and catalog without deleting it. Stock and sales history are kept; switch on again anytime.'}
              disabled={parent?.is_active === false}

              id="v-active"

            />
            <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
              Show in POS / Sales is set on the parent product and inherited here
              {variant.hide_in_pos ? ' (currently hidden in POS)' : ''}{variant.hide_in_sales ? ' (currently hidden in Sales)' : ''}.
            </p>

          </div>


          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Variant Image
            </label>
            <FileUpload
              accept="image/png,image/jpeg,image/webp"
              maxSize={MAX_RAW_UPLOAD_BYTES}
              onUpload={handleImage}
              preview={imageUrl || null}
              loading={imageUploading}
            />
          </div>
        </div>

        {/* Sticky action bar */}
        <div className="sticky bottom-0 mt-5 flex justify-end gap-3 border-t border-neutral-200 bg-white/90 py-3 backdrop-blur dark:border-neutral-700 dark:bg-neutral-900/90">
          <Button variant="secondary" onClick={() => confirmLeave(() => navigate(`/products/${id}`))}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handleSaveAndAddAnother}
            disabled={save.isPending || imageUploading}
            title="Save this variant and open the matrix to add more"
          >
            Save &amp; add another
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={save.isPending}
            // Saving no longer navigates away, so the disabled state is what
            // tells the cashier there is nothing left to save — the toast is
            // gone in a few seconds.
            disabled={!formDirty || save.isPending || imageUploading}
            title={formDirty ? 'Save this variant' : 'No changes to save'}
          >
            Save Variant
          </Button>
        </div>

        {leaveDialog}

        {/* Opens once the locations are known, so a multi-location SKU always asks which one. */}
        {showStockAdjust && stockRows && (
          <StockAdjustModal
            productId={variantId!}
            title={`Adjust Stock — ${variant.name}`}
            stockRows={stockRows}
            defaultLocationId={stockRows.length === 1 ? stockRows[0].location_id : undefined}
            onClose={() => setShowStockAdjust(false)}
            onAdjusted={() => {
              setShowStockAdjust(false);
              queryClient.invalidateQueries({ queryKey: ['product', variantId] });
              queryClient.invalidateQueries({ queryKey: ['product', id] });
              queryClient.invalidateQueries({ queryKey: ['products'] });
              toast({ title: 'Stock adjusted', variant: 'success' });
            }}
          />
        )}
      </PageContainer>
    </ErrorBoundary>
  );
}
